// auth.service.ts — PKCE OAuth flow for Cognito
//
// Why this file exists: the landing page has no backend session — authentication is
// a pure browser-to-Cognito exchange. This service owns the full PKCE lifecycle:
// generating the verifier/challenge pair, storing it in a domain-scoped cookie so it
// survives the cross-origin redirect to app.kaizenic.ai, and clearing all traces when done.
//
// What it deliberately does NOT do:
// - Refresh tokens — the main app handles session management once the user lands there
// - Decode or validate JWTs — that is the backend's responsibility
// - Show UI — errors are thrown to the caller
// - Store session tokens — the landing page never receives the OAuth callback; that
//   redirect goes to the main app. logout() clears the PKCE cookie on this origin
//   and delegates full session teardown to Cognito's hosted logout endpoint.
//
// Cross-origin verifier transport:
// The code_verifier is stored in a cookie with Domain=.kaizenic.ai so it is readable
// by both kaizenic.ai and app.kaizenic.ai. It is NEVER embedded in the state URL
// parameter — doing so would expose the verifier to server logs, CDN logs, and
// Referer headers. The state parameter carries only an unguessable CSRF nonce.

import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../environments/environment';

interface AuthStatePayload {
  nonce: string;
  timestamp: number;
  submissionData?: object;
}

interface PKCEPair {
  codeVerifier: string;
  codeChallenge: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private readonly CODE_VERIFIER_KEY = 'kaizenic_pkce_code_verifier';
  private readonly CODE_CHALLENGE_METHOD = 'S256';

  // ── PKCE generation ──────────────────────────────────────────────────────

  private async generateCodeVerifier(): Promise<string> {
    if (!this.isBrowser) {
      throw new Error('PKCE code generation requires browser environment');
    }

    // 32 random bytes → 43 base64url characters — within the required 43-128 range
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);

    return btoa(String.fromCharCode(...array))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  private async generateCodeChallenge(verifier: string): Promise<string> {
    if (!this.isBrowser) {
      throw new Error(
        'PKCE code challenge generation requires browser environment',
      );
    }

    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);

    const hashArray = Array.from(new Uint8Array(digest));
    return btoa(String.fromCharCode(...hashArray))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  private async generatePKCEPair(): Promise<PKCEPair> {
    const codeVerifier = await this.generateCodeVerifier();
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);
    return { codeVerifier, codeChallenge };
  }

  // ── Verifier storage (domain-scoped cookie) ───────────────────────────────

  // Domain=.kaizenic.ai makes the cookie readable by both kaizenic.ai and
  // app.kaizenic.ai. In local dev the attribute is omitted (defaults to current host).
  private get cookieDomain(): string {
    return window.location.hostname.endsWith('kaizenic.ai')
      ? '; Domain=.kaizenic.ai'
      : '';
  }

  private get cookieSecure(): string {
    return window.location.protocol === 'https:' ? '; Secure' : '';
  }

  private setCookieVerifier(verifier: string): void {
    if (!this.isBrowser) return;
    document.cookie =
      `${this.CODE_VERIFIER_KEY}=${verifier}` +
      `; Max-Age=300; Path=/${this.cookieDomain}; SameSite=Lax${this.cookieSecure}`;
  }

  private getCookieVerifier(): string | null {
    if (!this.isBrowser) return null;
    const match = document.cookie.match(
      new RegExp(`(?:^|; )${this.CODE_VERIFIER_KEY}=([^;]+)`),
    );
    return match ? decodeURIComponent(match[1]) : null;
  }

  private deleteCookieVerifier(): void {
    if (!this.isBrowser) return;
    document.cookie = `${this.CODE_VERIFIER_KEY}=; Max-Age=0; Path=/${this.cookieDomain}`;
  }

  getStoredCodeVerifier(): string | null {
    return this.getCookieVerifier();
  }

  clearCodeVerifier(): void {
    this.deleteCookieVerifier();
  }

  // ── Auth URL construction ─────────────────────────────────────────────────

  private async buildAuthUrl(
    action: 'login' | 'signup',
    submissionData?: object,
  ): Promise<string> {
    const { codeVerifier, codeChallenge } = await this.generatePKCEPair();

    this.setCookieVerifier(codeVerifier);

    // State carries only a CSRF nonce — never the code_verifier.
    const nonce = btoa(
      String.fromCharCode(...crypto.getRandomValues(new Uint8Array(16))),
    )
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');

    const statePayload: AuthStatePayload = {
      nonce,
      timestamp: Date.now(),
      ...(submissionData ? { submissionData } : {}),
    };

    const state = encodeURIComponent(JSON.stringify(statePayload));
    const baseUrl = `https://${environment.cognito.domain}`;

    const url = new URL(`${baseUrl}/${action}`);
    url.searchParams.set('client_id', environment.cognito.clientId);
    url.searchParams.set('response_type', 'code');
    url.searchParams.set('scope', 'email openid profile');
    url.searchParams.set('redirect_uri', environment.cognito.redirectUri);
    url.searchParams.set('code_challenge', codeChallenge);
    url.searchParams.set('code_challenge_method', this.CODE_CHALLENGE_METHOD);
    url.searchParams.set('state', state);

    return url.toString();
  }

  // ── Auth flow initiation ──────────────────────────────────────────────────

  private async initiateAuthFlow(
    action: 'login' | 'signup',
    submissionData?: object,
  ): Promise<void> {
    if (!this.isBrowser) {
      console.error(`${action} requires browser environment`);
      return;
    }

    try {
      const authUrl = await this.buildAuthUrl(action, submissionData);

      if (!this.getStoredCodeVerifier()) {
        throw new Error(
          'Failed to store authentication state. Please try again.',
        );
      }

      window.location.href = authUrl;
    } catch (error) {
      console.error(`Failed to initiate ${action}:`, error);
      throw error;
    }
  }

  async login(submissionData?: object): Promise<void> {
    return this.initiateAuthFlow('login', submissionData);
  }

  async signup(submissionData?: object): Promise<void> {
    return this.initiateAuthFlow('signup', submissionData);
  }

  // ── Session queries ───────────────────────────────────────────────────────

  isAuthenticated(): boolean {
    if (!this.isBrowser) return false;
    return sessionStorage.getItem('kaizenic_access_token') !== null;
  }

  logout(): void {
    if (!this.isBrowser) return;

    this.clearCodeVerifier();

    const logoutUrl =
      `https://${environment.cognito.domain}/logout` +
      `?client_id=${environment.cognito.clientId}` +
      `&logout_uri=${encodeURIComponent(environment.cognito.signOutUri)}`;

    window.location.href = logoutUrl;
  }
}
